import { Component, Inject, ViewChildren, QueryList, OnInit, AfterViewInit } from '@angular/core';
import {
    MatDialogRef,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import {
    FormControl,
    Validators,
    FormsModule,
    ReactiveFormsModule,
    FormGroup,
} from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckbox, } from '@angular/material/checkbox';
import { AsyncPipe } from '@angular/common';
import { IInterventionEntry } from '../../interfaces/intervention-entry-interface';
import { MsalService } from '@azure/msal-angular';
import { CdkScrollable, ScrollDispatcher, ScrollingModule } from '@angular/cdk/scrolling';
import { COMMON_ACTIONS } from '../../constants/common-actions.const';
import { ICommonAction } from '../../interfaces/common-action.interface';
import { HW_COMPONENTS } from '../../constants/hw-components.const';
import { SW_VERSIONS } from '../../constants/sw-versions.const';
import { CUSTOMERS } from '../../constants/customers.const';
import { ISSUE_GROUPS } from '../../constants/issue-groups.const';
import { IInterventionDialogData } from '../../interfaces/intervention-dialog-data.interface';
import { actionFormValidator } from '../../../directives/intervention-dialog-action-validator.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';


@Component({
    selector: 'intervention-dialog',
    templateUrl: 'intervention-dialog.component.html',
    styleUrl: 'intervention-dialog.component.scss',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        MatCheckbox,
        MatAutocompleteModule,
        AsyncPipe,
        ScrollingModule,
        CdkScrollable,
        MatIconModule,
        MatSelectModule
    ],
    providers: [
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { subscriptSizing: 'fixed' }
        }
    ]
})
export class InterventionDialogComponent implements OnInit {
    @ViewChildren(MatAutocompleteTrigger) autoCompleteTriggers: QueryList<MatAutocompleteTrigger>;

    hwHints = HW_COMPONENTS;
    swHints = SW_VERSIONS;
    customerHints = CUSTOMERS;
    issueGroupHints = ISSUE_GROUPS;
    commonActionHints = COMMON_ACTIONS;

    displayIssueGroup: boolean = true;
    title: string = '';

    dialogFormGroup = new FormGroup(
        {
            hwVersion: new FormControl('', [Validators.required]),
            swVersion: new FormControl('', [Validators.required]),
            customer: new FormControl('', [Validators.required]),
            cart: new FormControl('', []),
            usecase: new FormControl('', [Validators.required]),
            issueGroup: new FormControl('', []),
            minDowntime: new FormControl('', [Validators.required, Validators.min(1)]),
            description: new FormControl('', ([Validators.required, Validators.minLength(10),])),
            actions: new FormControl<string[]>([], [Validators.required]),
        });
    actionFormGroup = new FormGroup({
        commonAction: new FormControl<ICommonAction | string>(''),
        wheel: new FormControl(''),
        customAction: new FormControl(''),
    }, { validators: actionFormValidator() })

    filteredHwVersions: Observable<string[]>;
    filteredSwVersions: Observable<string[]>;
    filteredCustomers: Observable<string[]>;
    filteredIssueGroups: Observable<string[]>;
    filteredCommonAction: Observable<ICommonAction[]>;

    _localStorageHwVersionKey: string = 'hwVersion';
    _localStorageSwVersionKey: string = 'swVersion';
    _localStorageCustomerKey: string = 'customer';

    constructor(
        public dialogRef: MatDialogRef<InterventionDialogComponent>,
        private msalService: MsalService,
        @Inject(MAT_DIALOG_DATA) public data: IInterventionDialogData,
        public scrollDispatcher: ScrollDispatcher
    ) {
        this.scrollDispatcher.scrolled().subscribe((event: any) => {
            this.closePanels();
        });
    }

    ngOnInit() {
        this.filteredHwVersions = this.hwVersion.valueChanges.pipe(
            startWith(''),
            map(value => this._filterString(value || '', this.hwHints)),
        );
        this.filteredSwVersions = this.swVersion.valueChanges.pipe(
            startWith(''),
            map(value => this._filterString(value || '', this.swHints)),
        );
        this.filteredCustomers = this.customer.valueChanges.pipe(
            startWith(''),
            map(value => this._filterString(value || '', this.customerHints)),
        );
        this.filteredIssueGroups = this.issueGroup.valueChanges.pipe(
            startWith(''),
            map(value => this._filterString(value || '', this.issueGroupHints)),
        );
        this.filteredCommonAction = this.commonAction.valueChanges.pipe(
            startWith(''),
            map(value => {
                const action = typeof value === 'string' ? value : value?.action;
                return this._filterCommonAction(action as string, this.commonActionHints);
            })
        );

        this.displayIssueGroup = this.data.displayIssuegroup;
        this.title = this.data.title;

        if (!this.displayIssueGroup) {
            this.description.setValidators([]);
        } else {
            this.issueGroup.setValidators([Validators.required]);
        }

        this.hwVersion.setValue(localStorage.getItem(this._localStorageHwVersionKey));
        this.swVersion.setValue(localStorage.getItem(this._localStorageSwVersionKey));
        this.customer.setValue(localStorage.getItem(this._localStorageCustomerKey));
    }

    closePanels() {
        this.autoCompleteTriggers.forEach(trigger => {
            if (trigger.panelOpen) {
                trigger.closePanel();
            }
        });
    }

    get hwVersion() {
        return this.dialogFormGroup.get('hwVersion') as FormControl;
    }

    get swVersion() {
        return this.dialogFormGroup.get('swVersion') as FormControl;
    }

    get customer() {
        return this.dialogFormGroup.get('customer') as FormControl;
    }

    get issueGroup() {
        return this.dialogFormGroup.get('issueGroup') as FormControl;
    }

    get minDowntime() {
        return this.dialogFormGroup.get('minDowntime') as FormControl;
    }

    get description() {
        return this.dialogFormGroup.get('description') as FormControl;
    }

    get actions() {
        return this.dialogFormGroup.get('actions') as FormControl;
    }

    get commonAction() {
        return this.actionFormGroup.get('commonAction') as FormControl;
    }

    get usecase() {
        return this.dialogFormGroup.get('usecase') as FormControl;
    }

    get cart() {
        return this.dialogFormGroup.get('cart') as FormControl;
    }

    onOKClick(): void {
        if (!this.dialogFormGroup.valid) {
            var cn = alert('The form is not valid, please enter all required inputs');
            for (var i in this.dialogFormGroup.controls) {
                this.dialogFormGroup.get(i)?.markAsTouched();
            }
        } else {
            localStorage.setItem(this._localStorageHwVersionKey, this.dialogFormGroup.value.hwVersion!);
            localStorage.setItem(this._localStorageSwVersionKey, this.dialogFormGroup.value.swVersion!);
            localStorage.setItem(this._localStorageCustomerKey, this.dialogFormGroup.value.customer!);

            const interventionEntry: IInterventionEntry = {
                Actions: this.dialogFormGroup.value.actions!.join(",\n"),
                Customer: this.dialogFormGroup.value.customer!,
                Description: this.dialogFormGroup.value.description!,
                HwVersion: this.dialogFormGroup.value.hwVersion!,
                Downtime: parseInt(this.dialogFormGroup.value.minDowntime!),
                SwVersion: this.dialogFormGroup.value.swVersion!,
                Reporter: this.msalService.instance.getActiveAccount()?.name!,
                Timestamp: new Date(),
                IssueGroup: this.dialogFormGroup.value.issueGroup ?? null,
                Cart: this.dialogFormGroup.value.cart ?? null,
                Usecase: this.dialogFormGroup.value.usecase ?? null,
                BugId: null,  // Set to null
            }
            this.dialogRef.close(interventionEntry);
        }
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    private _filterString(value: string, options: string[]): string[] {
        const filterValue = value.toLowerCase();
        return options.filter(option => option.toLowerCase().includes(filterValue));
    }

    private _filterCommonAction(value: string, options: ICommonAction[]): ICommonAction[] {
        const filterValue = value.toLowerCase();
        return options.filter(option => option.action.toLowerCase().includes(filterValue));
    }

    displayAction(option: any): string {
        return option ? option.action : '';  // Display the 'action' field if option is selected
    }

    submitAction() {
        if (!this.actionFormGroup.valid) {
            return;
        }

        if (this.actionFormGroup.value.commonAction) {
            let actionString = (this.actionFormGroup.value.commonAction as ICommonAction)?.action;
            actionString += this.actionFormGroup.value.wheel ? ` on ${this.actionFormGroup.value.wheel}` : '';
            actionString += this.actionFormGroup.value.customAction ? ` ${this.actionFormGroup.value.customAction}` : '';

            this.actions.setValue([...this.actions.value, actionString]);
            this.actionFormGroup.setValue({ commonAction: '', wheel: '', customAction: '' });
            return;
        }

        this.actions.setValue([...this.actions.value, this.actionFormGroup.value.customAction]);
        this.actionFormGroup.setValue({ commonAction: '', wheel: '', customAction: '' });
    }

    deleteAction(index: number) {
        this.actions.value.splice(index, 1);
        this.actions.updateValueAndValidity();
    }
}
